import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from '../../i18n/LanguageContext';
import {
  IconPhone,
  IconCheck,
  IconMapPin,
  IconAlertTriangle,
  IconUser,
  IconTractor,
  IconClock,
} from '@tabler/icons-react';

export default function EmergencyOverlay({
  emergency,
  isCommunity = false,
  onResolve,
  onRespond,
}) {
  const { t, language } = useTranslation();
  const { userProfile, role } = useAuth();
  const [elapsed, setElapsed] = useState(0);
  const [responding, setResponding] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [hasResponded, setHasResponded] = useState(false);

  // Elapsed time counter
  useEffect(() => {
    const interval = setInterval(() => {
      if (!emergency?.triggeredAt) return;
      const triggeredAt = emergency.triggeredAt.toDate
        ? emergency.triggeredAt.toDate()
        : new Date(emergency.triggeredAt);
      const seconds = Math.floor((Date.now() - triggeredAt.getTime()) / 1000);
      setElapsed(seconds);
    }, 1000);
    return () => clearInterval(interval);
  }, [emergency]);

  // Flash document title
  useEffect(() => {
    let flashing = true;
    const original = document.title;
    const interval = setInterval(() => {
      document.title = flashing ? '\u{1F6A8} ' + t('emergency.state_alert') : original;
      flashing = !flashing;
    }, 800);
    return () => {
      clearInterval(interval);
      document.title = original;
    };
  }, []);

  const formatElapsed = (s) => {
    if (s < 60) return `${s}s`;
    const m = Math.floor(s / 60);
    const rem = s % 60;
    return `${m}m ${rem}s`;
  };

  const formatTime = (ts) => {
    if (!ts) return '—';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleTimeString(language, { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  const handleRespond = async () => {
    if (!emergency?.id || hasResponded) return;
    setResponding(true);
    await onRespond(emergency.id);
    setHasResponded(true);
    setResponding(false);
  };

  const handleResolve = async () => {
    if (!emergency?.id) return;
    setResolving(true);
    await onResolve(emergency.id);
    setResolving(false);
  };

  const handleCall999 = () => {
    // 999 in the UK, 112 across Europe. Kept in the translation table so the
    // number dialled and the number shown on screen cannot drift apart.
    window.location.href = 'tel:' + t('emergency.tel_number');
  };

  const handleCallOperator = () => {
    if (emergency?.operatorPhone) {
      window.location.href = `tel:${emergency.operatorPhone}`;
    }
  };

  const canResolve = role === 'org_admin' || role === 'manager';

  // Three states, deliberately distinct at a glance across a room.
  //
  //   amber   an automatic alarm is still inside its response window
  //   orange  the operator has said they need help — either by raising a
  //           manual SOS, or by answering an automatic alarm once the mobile
  //           app records that (operatorResponse)
  //   red     the response window closed with no answer, or 999 has fired
  //
  // Until the mobile app writes operatorResponse, a manual SOS is the only
  // route to orange — which is correct, since raising one *is* asking for help.
  const respondWindow = Number(emergency && emergency.countdown) || 60;
  const secondsLeft = Math.max(0, respondWindow - elapsed);
  const operatorResponse = emergency && emergency.operatorResponse;

  let state = 'amber';
  if (emergency && emergency.call999FiredAt) state = 'red';
  else if (operatorResponse === 'needs_help' || (emergency && emergency.triggerType === 'manual')) state = 'orange';
  else if (operatorResponse === 'okay') state = 'amber'; // said they are fine; never escalate them
  else if (elapsed >= respondWindow) state = 'red';

  const STATES = {
    amber:  { bg: 'rgba(143, 106, 0, 0.97)',  headline: t('emergency.state_awaiting')     },
    orange: { bg: 'rgba(194, 87, 10, 0.97)',  headline: t('emergency.state_needs_help')   },
    red:    { bg: 'rgba(163, 45, 45, 0.97)',  headline: (emergency && emergency.call999FiredAt)
                                                ? t('emergency.state_alert')
                                                : t('emergency.state_no_response') },
  };
  const tone = STATES[state];

  const triggerLabels = {
    impact:      t('incidents.impact_detected'),
    manual:      t('incidents.manual_sos'),
    man_down:    t('incidents.man_down'),
    no_movement: t('emergency.no_movement'),
  };

  if (!emergency) return null;

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      background: tone.bg,
      zIndex: 9999,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 32,
      overflow: 'auto',
    }}>

      {/* Pulsing background animation */}
      <style>{`
        @keyframes pulse-bg {
          0%, 100% { background: rgba(163,45,45,0.97); }
          50% { background: rgba(120,20,20,0.97); }
        }
        @keyframes pulse-ring {
          0% { transform: scale(1); opacity: 1; }
          100% { transform: scale(2); opacity: 0; }
        }
        .emergency-overlay { animation: pulse-bg 2s infinite; }
        .pulse-ring {
          position: absolute;
          inset: 0;
          border-radius: 50%;
          border: 2px solid rgba(255,255,255,0.4);
          animation: pulse-ring 1.5s infinite;
        }
      `}</style>

      {/* Community badge */}
      {isCommunity && (
        <div style={{
          background: 'rgba(255,255,255,0.15)',
          color: '#fff',
          fontSize: 11,
          padding: '4px 12px',
          borderRadius: 'var(--radius-full)',
          marginBottom: 16,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
        }}>
          {t('emergency.community_badge')}
        </div>
      )}

      {/* Alert header */}
      <div style={{ textAlign: 'center', marginBottom: 28 }}>
        <div style={{
          position: 'relative',
          width: 64,
          height: 64,
          margin: '0 auto 16px',
        }}>
          <div className="pulse-ring" />
          <div style={{
            width: 64,
            height: 64,
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <IconAlertTriangle size={32} color="#fff" stroke={1.5} />
          </div>
        </div>
        <div style={{ fontSize: 28, fontWeight: 600, color: '#fff', marginBottom: 6 }}>
          {tone.headline}
        </div>
        <div style={{ fontSize: 14, color: 'rgba(255,255,255,0.7)' }}>
          {triggerLabels[emergency.triggerType] || t('emergency.triggered_generic')} · {formatTime(emergency.triggeredAt)}
        </div>
        {state === 'amber' && (
          <div style={{ fontSize: 14, color: 'rgba(255,255,255,0.9)', marginTop: 6 }}>
            {t('emergency.seconds_to_respond').replace('{n}', secondsLeft)}
          </div>
        )}
      </div>

      {/* Main content */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 16,
        width: '100%',
        maxWidth: 800,
        marginBottom: 20,
      }}>

        {/* Operator details */}
        <div style={{
          background: 'rgba(255,255,255,0.1)',
          borderRadius: 'var(--radius-lg)',
          padding: '18px 20px',
          border: '0.5px solid rgba(255,255,255,0.2)',
        }}>
          <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.5)', letterSpacing: '0.08em', marginBottom: 12, textTransform: 'uppercase' }}>{t('role.operator')}</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
            <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <IconUser size={20} color="#fff" stroke={1.5} />
            </div>
            <div>
              <div style={{ fontSize: 16, color: '#fff', fontWeight: 500 }}>{emergency.triggeredByName || '—'}</div>
              {isCommunity && emergency.orgName && (
                <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)' }}>{emergency.orgName}</div>
              )}
            </div>
          </div>
          {emergency.machineId && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <IconTractor size={15} color="rgba(255,255,255,0.6)" stroke={1.5} />
              <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.8)' }}>{t('emergency.machine_involved')}</div>
            </div>
          )}
          {emergency.impactForce && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <IconAlertTriangle size={15} color="rgba(255,255,255,0.6)" stroke={1.5} />
              <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.8)' }}>{t('emergency.impact_label')}: {emergency.impactForce}G</div>
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <IconClock size={15} color="rgba(255,255,255,0.6)" stroke={1.5} />
            <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.8)' }}>
              {formatElapsed(elapsed)} {t('emergency.ago')}
            </div>
          </div>
        </div>

        {/* Location */}
        <div style={{
          background: 'rgba(255,255,255,0.1)',
          borderRadius: 'var(--radius-lg)',
          padding: '18px 20px',
          border: '0.5px solid rgba(255,255,255,0.2)',
        }}>
          <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.5)', letterSpacing: '0.08em', marginBottom: 12, textTransform: 'uppercase' }}>{t('emergency.location')}</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <IconMapPin size={16} color="rgba(255,255,255,0.6)" stroke={1.5} />
            {emergency.location?.what3words ? (
              <div style={{ fontSize: 16, color: '#fff', fontFamily: 'var(--font-mono)', fontWeight: 500 }}>
                ///{emergency.location.what3words}
              </div>
            ) : (
              <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)' }}>{t('emergency.location_unavailable')}</div>
            )}
          </div>
          {emergency.location?.lat && emergency.location?.lng && (
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', fontFamily: 'var(--font-mono)', marginBottom: 12 }}>
              {emergency.location.lat.toFixed(6)}, {emergency.location.lng.toFixed(6)}
            </div>
          )}
          {emergency.location?.lat && (
            <a
              href={`https://www.google.com/maps?q=${emergency.location.lat},${emergency.location.lng}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5,
                fontSize: 12,
                color: 'rgba(255,255,255,0.7)',
                background: 'rgba(255,255,255,0.1)',
                padding: '5px 10px',
                borderRadius: 'var(--radius-md)',
                textDecoration: 'none',
              }}
            >
              {t('emergency.open_in_maps')} →
            </a>
          )}
        </div>
      </div>

      {/* Responders */}
      {emergency.responders?.length > 0 && (
        <div style={{
          width: '100%',
          maxWidth: 800,
          background: 'rgba(255,255,255,0.1)',
          borderRadius: 'var(--radius-lg)',
          padding: '14px 20px',
          border: '0.5px solid rgba(255,255,255,0.2)',
          marginBottom: 20,
        }}>
          <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.5)', letterSpacing: '0.08em', marginBottom: 10, textTransform: 'uppercase' }}>{t('emergency.panel_responders')}</div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {emergency.responders.map((r, i) => (
              <div key={i} style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                background: 'rgba(255,255,255,0.15)',
                borderRadius: 'var(--radius-full)',
                padding: '4px 10px',
              }}>
                <IconCheck size={12} color="#fff" stroke={2} />
                <span style={{ fontSize: 12, color: '#fff' }}>
                  {r.name}{r.distanceMiles ? ` · ${r.distanceMiles} mi` : ''}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 999 called indicator */}
      {emergency.call999FiredAt && (
        <div style={{
          width: '100%',
          maxWidth: 800,
          background: 'rgba(255,255,255,0.15)',
          borderRadius: 'var(--radius-md)',
          padding: '10px 16px',
          marginBottom: 20,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}>
          <IconPhone size={15} color="#fff" stroke={1.5} />
          <span style={{ fontSize: 13, color: '#fff' }}>
            {t('emergency.called_automatically_at').replace('{time}', formatTime(emergency.call999FiredAt))}
          </span>
        </div>
      )}

      {/* Action buttons */}
      <div style={{
        display: 'flex',
        gap: 10,
        flexWrap: 'wrap',
        justifyContent: 'center',
        maxWidth: 800,
        width: '100%',
      }}>
        {/* Call operator */}
        <button
          onClick={handleCallOperator}
          style={{
            background: 'rgba(255,255,255,0.2)',
            color: '#fff',
            border: '0.5px solid rgba(255,255,255,0.3)',
            borderRadius: 'var(--radius-md)',
            padding: '12px 20px',
            fontSize: 14,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <IconPhone size={16} stroke={1.5} />
          Call {emergency.triggeredByName?.split(' ')[0] || 'operator'}
        </button>

        {/* Call 999 */}
        <button
          onClick={handleCall999}
          style={{
            background: '#fff',
            color: 'var(--color-danger)',
            border: 'none',
            borderRadius: 'var(--radius-md)',
            padding: '12px 20px',
            fontSize: 14,
            fontWeight: 600,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <IconPhone size={16} stroke={2} />
          Call 999
        </button>

        {/* I'm responding */}
        {!isCommunity && !hasResponded && (
          <button
            onClick={handleRespond}
            disabled={responding}
            style={{
              background: 'rgba(255,255,255,0.2)',
              color: '#fff',
              border: '0.5px solid rgba(255,255,255,0.3)',
              borderRadius: 'var(--radius-md)',
              padding: '12px 20px',
              fontSize: 14,
              cursor: responding ? 'not-allowed' : 'pointer',
              opacity: responding ? 0.7 : 1,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <IconCheck size={16} stroke={1.5} />
            {responding ? t('emergency.confirming') : t('emergency.im_responding')}
          </button>
        )}

        {hasResponded && (
          <div style={{
            background: 'rgba(255,255,255,0.1)',
            color: 'rgba(255,255,255,0.7)',
            border: '0.5px solid rgba(255,255,255,0.2)',
            borderRadius: 'var(--radius-md)',
            padding: '12px 20px',
            fontSize: 14,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}>
            <IconCheck size={16} stroke={1.5} />
            You are responding
          </div>
        )}

        {/* Resolve — managers only */}
        {canResolve && !isCommunity && (
          <button
            onClick={handleResolve}
            disabled={resolving}
            style={{
              background: 'rgba(255,255,255,0.1)',
              color: 'rgba(255,255,255,0.6)',
              border: '0.5px solid rgba(255,255,255,0.2)',
              borderRadius: 'var(--radius-md)',
              padding: '12px 20px',
              fontSize: 13,
              cursor: resolving ? 'not-allowed' : 'pointer',
              opacity: resolving ? 0.5 : 1,
            }}
          >
            {resolving ? t('emergency.resolving') : t('emergency.mark_resolved')}
          </button>
        )}
      </div>

      {/* Community dismiss note */}
      {isCommunity && (
        <div style={{
          marginTop: 20,
          fontSize: 12,
          color: 'rgba(255,255,255,0.4)',
          textAlign: 'center',
        }}>
          This alert will dismiss automatically when the emergency is resolved
        </div>
      )}
    </div>
  );
}